import { Alert, Box, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { offeredSwatches } from '../../shared/catalogue-diff'
import type { Change } from '../../shared/catalogue-diff'
import { composeAvatar, defaultOptionFor } from '../../shared/compose-avatar'
import {
  baseLayerUrl,
  referenceLayerUrl,
  TRY_ON_SLUG,
  withTryOnOption,
} from '../../shared/try-on'
import type { BoCatalog, BoSection, LayerUrls } from '../../shared/types'
import type { measureBounds as MeasureBounds } from './measureBounds'
import type { ObjectUrlMaker } from './objectUrlLayers'
import { revokeAll, tryOnLayersFrom } from './objectUrlLayers'
import PlacementWarning from './PlacementWarning'
import PreviewCanvas from './PreviewCanvas'
import PreviewOptionStrip from './PreviewOptionStrip'
import PreviewSectionList from './PreviewSectionList'
import TierSwitcher from './TierSwitcher'
import usePlacementVerdict from './usePlacementVerdict'

export interface TryOn {
  files: File[]
  sectionId: string
}

export interface PreviewStudioProps {
  catalogue: BoCatalog
  initialSectionId?: string
  tryOn?: TryOn
  /** How this catalogue differs from LIVE, so the strip can mark what is new and what is gone. */
  changes?: Change[]
  onAddOption?: (sectionId: string) => void
  urls?: ObjectUrlMaker
  measure?: typeof MeasureBounds
}

interface Pick {
  sectionId: string
  slug?: string
  swatch?: string
}

export default function PreviewStudio({
  catalogue,
  initialSectionId,
  tryOn,
  changes,
  onAddOption,
  urls,
  measure,
}: PreviewStudioProps) {
  const { formatMessage } = useIntl()
  const paintable = catalogue.sections
  const firstSection = tryOn?.sectionId ?? initialSectionId ?? paintable[0]?.id

  const [tier, setTier] = useState(5)
  const [pick, setPick] = useState<Pick>({ sectionId: firstSection ?? '' })
  const [tryOnUrls, setTryOnUrls] = useState<LayerUrls>({})

  useEffect(() => {
    if (!tryOn || tryOn.files.length === 0) {
      return undefined
    }
    const built = tryOnLayersFrom(tryOn.files, urls)
    setTryOnUrls(built.layers)

    // Object URLs pin the whole file in memory until revoked, so closing must free them.
    return () => revokeAll(built.urls, urls)
  }, [tryOn, urls])

  const hasTryOn = Object.keys(tryOnUrls).length > 0

  const working = useMemo(
    () =>
      hasTryOn && tryOn
        ? withTryOnOption(
            catalogue,
            tryOn.sectionId,
            tryOnUrls,
            formatMessage({ id: 'avatarStudio.preview.tryOnName' }),
          )
        : catalogue,
    [catalogue, formatMessage, hasTryOn, tryOn, tryOnUrls],
  )

  const section =
    working.sections.find((s) => s.id === pick.sectionId) ?? working.sections[0]

  const chooseSection = (sectionId: string) => {
    const next = working.sections.find((s) => s.id === sectionId)
    setPick({
      sectionId,
      slug:
        next?.kind === 'color'
          ? undefined
          : defaultOptionFor(working, sectionId)?.slug,
      swatch: next?.kind === 'color' ? firstSwatch(next) : undefined,
    })
  }

  const activeSlug =
    hasTryOn && tryOn?.sectionId === section?.id && pick.slug === undefined
      ? TRY_ON_SLUG
      : pick.slug ?? (section ? defaultOptionFor(working, section.id)?.slug : undefined)

  const composed = useMemo(
    () =>
      composeAvatar({
        catalogue: working,
        selectedSectionId: section?.id,
        selectedSlug: activeSlug,
        selectedSwatch: pick.swatch,
      }),
    [working, section?.id, activeSlug, pick.swatch],
  )

  const verdict = usePlacementVerdict({
    candidateUrl: hasTryOn ? baseLayerUrl(tryOnUrls) : undefined,
    referenceUrl: tryOn ? referenceLayerUrl(catalogue, tryOn.sectionId) : undefined,
    measure,
  })

  if (!section) {
    return (
      <Alert severity="info">
        {formatMessage({ id: 'avatarStudio.preview.noSections' })}
      </Alert>
    )
  }

  const sectionName = section.displayName ?? section.id

  return (
    <Stack spacing={2}>
      <PlacementWarning verdict={verdict} sectionName={sectionName} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Stack spacing={1} alignItems="center" sx={{ flexGrow: 1, minWidth: 320 }}>
          <PreviewCanvas
            composed={composed}
            selectedSectionId={section.id}
            alt={formatMessage(
              { id: 'avatarStudio.preview.canvasAria' },
              { set: catalogue.set.name, section: sectionName },
            )}
          />
          <TierSwitcher tier={tier} onChange={setTier} />
        </Stack>

        <Box sx={{ width: 240, flexShrink: 0 }}>
          {tryOn ? (
            <Typography variant="body2" color="text.secondary">
              {formatMessage(
                { id: 'avatarStudio.preview.tryOnScope' },
                { section: sectionName },
              )}
            </Typography>
          ) : (
            <PreviewSectionList
              sections={paintable}
              selectedId={section.id}
              onSelect={chooseSection}
            />
          )}
        </Box>
      </Box>

      <Stack spacing={0.5}>
        <Typography variant="overline" color="text.secondary" component="h3">
          {formatMessage(
            { id: 'avatarStudio.preview.optionsOf' },
            { section: sectionName },
          )}
        </Typography>
        <PreviewOptionStrip
          catalogue={working}
          section={section}
          tier={tier}
          changes={changes}
          selectedSlug={activeSlug}
          selectedSwatch={pick.swatch ?? firstSwatch(section)}
          onPickOption={(slug) => setPick((p) => ({ ...p, slug }))}
          onPickSwatch={(swatch) => setPick((p) => ({ ...p, swatch }))}
          onAddOption={onAddOption ? () => onAddOption(section.id) : undefined}
        />
      </Stack>
    </Stack>
  )
}

const firstSwatch = (section: BoSection): string | undefined =>
  offeredSwatches(section.swatches, section.retiredSwatches)[0]
